// src/context/HealthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { healthMonitor } from '../services/healthMonitor';
import { logger } from '../utils/logger';

const HealthContext = createContext();
const CID = 'health-context';

export const HealthProvider = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(healthMonitor.status);
  const [authReady, setAuthReady] = useState(false);

  logger.trace(CID, 'HealthProvider render', {
    user: !!user,
    authReady,
    aiEnabled: status.ai.enabled,
    backendHealthy: status.backend.healthy
  });

  useEffect(() => {
    if (!authReady && user !== undefined) {
      logger.debug(CID, 'Auth ready, starting health monitoring');
      setAuthReady(true);
    }
  }, [user]);

  useEffect(() => {
    if (!authReady) {
      logger.trace(CID, 'Auth not ready, delaying health monitoring');
      return;
    }

    const unsubscribe = healthMonitor.subscribe((newStatus) => {
      logger.trace(CID, 'Health status updated', {
        aiChanged: status.ai.enabled !== newStatus.ai.enabled,
        backendChanged: status.backend.healthy !== newStatus.backend.healthy
      });
      setStatus(newStatus);
    });
    
    healthMonitor.startMonitoring();
    
    return () => {
      logger.debug(CID, 'HealthProvider cleanup');
      unsubscribe();
      healthMonitor.stopMonitoring();
    };
  }, [authReady]);

  return (
    <HealthContext.Provider value={status}>
      {children}
    </HealthContext.Provider>
  );
};

export const useHealth = () => {
  const context = useContext(HealthContext);
  
  if (context === undefined) {
    throw new Error('useHealth must be used within a HealthProvider');
  }
  
  return context;
};